import {
  Component,
  HostListener,
  OnInit,
  OnDestroy,
  HostBinding,
  ViewChild,
  Renderer2,
  Inject,
  DOCUMENT,
} from '@angular/core';

import { CommonService } from 'src/app/shared/service/common.service';
import { environment } from 'src/environments/environment';
import { AuthService } from './core/services/auth.service';
import { NavigationEnd, Router } from '@angular/router';
import { AuthenticationService } from './shared/service/authentication.service';
import { filter } from 'rxjs/operators';
import { BreadcrumpsService } from './core/services/breadcrumps.service';
import { UtilService } from './core/services/util.service';
import {
  NPS,
  LIVE_SHARE,
  LOGIN,
  LOGOUT,
  RESET_PASSWORD,
  SAVINGS_CALCULATOR,
  VERIFY_EMAIL,
} from './shared/routeLink.constant';
import { IdleService } from './shared/service/idle.service';
import { RoleService } from './shared/service/role.service';
import { Store } from '@ngrx/store';
import { logout } from './store/user/user.actions';
import { selectSelectedVehicle } from './store/user/user.selectors';
import { LoadUserMenuList } from './store/userMenuList/userMenuList.action';
import { setAllCountries } from './store/appContext/app-context.actions';
import { AlertSessionService } from './core/services/alert-session.service';
import { SidenavComponent } from './core/components/sidenav/sidenav.component'; // adjust path

interface SideNavToggle {
  screenWidth: number;
  collapsed: boolean;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild('sidenavRef') sidenavRef!: SidenavComponent;

  title = 'oneportal';
  activePage = '';
  public restrictPages = [
    LIVE_SHARE,
    LOGIN,
    VERIFY_EMAIL,
    RESET_PASSWORD,
    SAVINGS_CALCULATOR,
  ];
  public resetLocalStorage = [
    LIVE_SHARE,
    LOGIN,
    VERIFY_EMAIL,
    RESET_PASSWORD,
    SAVINGS_CALCULATOR,
    NPS,
  ];
  constructor(
    public authService: AuthService,
    private readonly router: Router,
    private readonly AuthenticationService: AuthenticationService,
    private readonly _breadcrumpsService: BreadcrumpsService,
    private readonly utilService: UtilService,
    private readonly idleService: IdleService,
    private readonly roleService: RoleService,
    private readonly alertSessionService: AlertSessionService,
    private readonly store: Store,
    private readonly commonService: CommonService,
    private readonly renderer: Renderer2,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        const urlPath = event.url.split('?')[0]; // removes the query string
        this.activePage = urlPath.split('/')[1]; // gets the first path segment
        this._breadcrumpsService.setValue(event);
        if (
          (!localStorage.getItem('userState') ||
            !localStorage.getItem('token')) &&
          !this.resetLocalStorage.includes(this.activePage)
        ) {
          localStorage.clear();
          this.router.navigateByUrl(LOGOUT);
        }
        this.updateBodyScrollClass();
      });
  }

  isSideNavCollapsed = true;
  screenWidth = window.innerWidth;
  isLoggedIn = false;
  isOpened: any = true;
  selectedVehicle: any;

  ngOnInit() {
    this.store.select(selectSelectedVehicle).subscribe((vehicle) => {
      const previousVehicle = this.selectedVehicle;
      this.selectedVehicle = vehicle ?? null;

      if (
        this.isLoggedIn &&
        vehicle &&
        previousVehicle &&
        previousVehicle.code !== vehicle.code
      ) {
        this.alertSessionService.reset();
        return;
      }
      if (this.isLoggedIn && vehicle) {
        this.alertSessionService.init();
      }
    });

    this.AuthenticationService.currentAuthStatus.subscribe((user) => {
      if (user) {
        this.isLoggedIn = true;
        this.idleService.startWatching();
        if (this.selectedVehicle) {
          this.alertSessionService.init();
        }
      } else {
        this.isLoggedIn = false;
        this.idleService.stopWatching();
        this.alertSessionService.stop();
      }
      this.updateBodyScrollClass();
    });
    if (this.selectedVehicle) {
      this.loadMenuLinks(this.selectedVehicle?.code);
    }
    if (this.commonService.getAccessToken()) {
      this.loadAllCountries();
    }
    this.roleService.initializeRoleFromStorage();
    this.updateBodyScrollClass();
  }

  @HostBinding('attr.app-version') appVersion = environment.appVersion;
  @HostBinding('attr.app-name') appName = environment.appName;
  @HostListener('window:storage', ['$event'])
  onStorageChange(event: StorageEvent) {
    if (event.key === null) {
      // Local storage has been cleared
      this.router.navigateByUrl(LOGOUT);
    }
  }

  onToggleSideNav(data: SideNavToggle): void {
    this.screenWidth = data.screenWidth;
    this.isSideNavCollapsed = data.collapsed;
    this.isOpened = data.collapsed;
  }

  loadMenuLinks(vehicleCode: string) {
    if (this.commonService.getConsentPending()) {
      this.utilService.redirectToLogout();
      return;
    }
    this.utilService.loadMenuLinks(vehicleCode).subscribe({
      next: (navData: any) => {
        this.store.dispatch(LoadUserMenuList({ menu: navData }));
      },
      error: (error) => {
        this.utilService.logoutApi().subscribe((res: any) => {
          this.commonService.clearLocalStorageItems();
          sessionStorage.clear();
          this.router.navigate(['/login']);
          this.store.dispatch(logout());
        });
      },
    });
  }

  loadAllCountries() {
    this.utilService.loadCountryList().subscribe({
      next: (countries) => {
        this.store.dispatch(setAllCountries({ allCountries: countries ?? [] }));
      },
      error: (error) => {
        console.error(
          '[AppContext] AppComponent: failed to load countries',
          error
        );
      },
    });
  }

  showMenubar() {
    if (
      this.isLoggedIn === true &&
      !this.restrictPages.includes(this.activePage) &&
      !this.commonService.getConsentPending()
    ) {
      return true;
    }
    return false;
  }

  private updateBodyScrollClass(): void {
    if (this.showMenubar()) {
      this.renderer.addClass(this.document.body, 'app-shell-active');
    } else {
      this.renderer.removeClass(this.document.body, 'app-shell-active');
    }
  }

  get showWidget(): boolean {
    return this.isLoggedIn && localStorage.getItem('impersonate') === 'true';
  }

  onHeaderMenuToggle(): void {
    this.sidenavRef.toggleCollapse();
  }

  ngOnDestroy() {
    this.idleService.stopWatching();
    this.alertSessionService.stop();
    this.renderer.removeClass(this.document.body, 'app-shell-active');
  }
}
